import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PortfolioPage = () => {
  const [portfolio, setPortfolio] = useState([]);
  const [filter, setFilter] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchPortfolio = async () => {
      try {
        const res = await axios.get(`${API}/portfolio`);
        setPortfolio(res.data);
      } catch (error) {
        console.error('Error fetching portfolio:', error);
      }
    };
    fetchPortfolio();
  }, []);

  const getImages = (item) => {
    if (item.images && item.images.length > 0) return item.images;
    if (item.image_url) return [item.image_url];
    return [];
  };

  const categories = ['All', ...new Set(portfolio.map((item) => item.category))];
  const filteredPortfolio = filter === 'All' 
    ? portfolio 
    : portfolio.filter((item) => item.category === filter);

  const openLightbox = (item) => {
    setSelectedItem(item);
    setCurrentImageIndex(0);
  };

  const images = selectedItem ? getImages(selectedItem) : [];

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(i => (i - 1 + images.length) % images.length);
  };

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex(i => (i + 1) % images.length);
  };

  return (
    <div data-testid="portfolio-page" className="bg-[#F5F5F0]">
      {/* Hero */}
      <section className="relative h-[60vh] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1529636798458-92182e662485?w=1920')` }}
        />
        <div className="absolute inset-0 bg-[#4A5D23]/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center text-white px-4">
          <span className="font-accent text-4xl text-[#D4AF37] mb-4">Our Work</span>
          <h1 className="font-heading text-5xl md:text-7xl font-light">Portfolio</h1>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {categories.map((category) => (
              <Button
                key={category}
                data-testid={`filter-${category.toLowerCase()}`}
                variant={filter === category ? 'default' : 'outline'}
                onClick={() => setFilter(category)}
                className={`rounded-full px-6 ${
                  filter === category
                    ? 'bg-[#4A5D23] hover:bg-[#6B7F3A] text-white'
                    : 'border-[#4A5D23] text-[#4A5D23] hover:bg-[#4A5D23] hover:text-white'
                }`}
              >
                {category}
              </Button>
            ))}
          </div>

          {/* Grid */}
          <div className="bento-grid">
            {filteredPortfolio.map((item, index) => {
              const imgs = getImages(item);
              return (
                <div
                  key={item.id}
                  data-testid={`portfolio-card-${index}`}
                  onClick={() => openLightbox(item)}
                  className={`relative overflow-hidden rounded-2xl group cursor-pointer img-hover-zoom ${
                    index === 0 || index === 3 ? 'bento-item-large' : ''
                  }`}
                  style={{ aspectRatio: index === 0 || index === 3 ? '1' : '3/4' }}
                >
                  <img src={imgs[0] || item.image_url} alt={item.title} className="w-full h-full object-cover" />
                  {imgs.length > 1 && (
                    <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-sm">
                      {imgs.length} photos
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#2C2C2C]/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                    <span className="text-[#D4AF37] text-sm tracking-wider uppercase mb-2">{item.category}</span>
                    <h3 className="font-heading text-2xl text-white">{item.title}</h3>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Gallery Lightbox */}
      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-5xl p-0 overflow-hidden bg-white">
          {selectedItem && (
            <>
              {/* Image viewer */}
              <div className="relative bg-[#1a1a1a]">
                <img
                  src={images[currentImageIndex]}
                  alt={`${selectedItem.title} - Photo ${currentImageIndex + 1}`}
                  className="w-full h-[65vh] object-contain"
                />

                {/* Navigation arrows */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      data-testid="gallery-prev"
                      className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronLeft size={22} />
                    </button>
                    <button
                      onClick={nextImage}
                      data-testid="gallery-next"
                      className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-colors"
                    >
                      <ChevronRight size={22} />
                    </button>
                    {/* Counter */}
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>

              {/* Thumbnail strip */}
              {images.length > 1 && (
                <div className="flex gap-1.5 p-3 overflow-x-auto bg-[#f5f5f0]">
                  {images.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentImageIndex(i)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        i === currentImageIndex ? 'border-[#D4AF37] ring-1 ring-[#D4AF37]' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={url} alt={`Thumb ${i + 1}`} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}

              {/* Info */}
              <div className="p-6">
                <DialogHeader>
                  <span className="text-[#D4AF37] text-sm tracking-wider uppercase">{selectedItem.category}</span>
                  <DialogTitle className="font-heading text-3xl text-[#2C2C2C]">{selectedItem.title}</DialogTitle>
                </DialogHeader>
                {selectedItem.description && (
                  <p className="text-[#6B7280] mt-4">{selectedItem.description}</p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioPage;
